import type { CartItem, DeliveryMethod } from './types'
import {
  currencySignHtml,
  renderWithCurrency
} from 'application/utils/currency'
import { CounterField } from 'application/ui/fields/counter-field'
import {
  clearCart,
  getCartItems,
  removeFromCart,
  updateCartItem
} from './_service'

export class CartList {
  private container: HTMLElement
  private items: CartItem[] = []
  private deliveryCost: number = 0
  private baseDeliveryCost: number = 0
  private discountAmount: number = 0
  private totalPrice: number = 0
  private totalPriceElement: HTMLElement
  private deliveryCostElement: HTMLElement | null = null
  private itemsContainer: HTMLElement
  private liveRegionResetTimer: number | null = null
  private onItemsChange?: (_items: CartItem[]) => void

  constructor(
    container: HTMLElement,
    onItemsChange?: (_items: CartItem[]) => void
  ) {
    this.container = container
    this.onItemsChange = onItemsChange

    this.init()
  }

  private init() {
    console.debug('CartList initialized')

    this.deliveryCostElement = this.container.querySelector<HTMLElement>(
      '[data-delivery-cost]'
    )
    if (this.deliveryCostElement) {
      this.baseDeliveryCost = parseFloat(
        this.deliveryCostElement.dataset.deliveryCost || '0'
      )
      this.deliveryCost = this.baseDeliveryCost
    }

    const discountElement =
      this.container.querySelector<HTMLElement>('[data-discount]')
    if (discountElement) {
      this.discountAmount = parseFloat(discountElement.dataset.discount || '0')
    }

    this.totalPriceElement =
      this.container.querySelector<HTMLElement>('[data-total-price]')!
    this.itemsContainer =
      this.container.querySelector<HTMLElement>('[data-cart-items]')!

    this.container.addEventListener(
      'counterfield:change',
      this.onCounterChange as EventListener
    )

    this.updateItems(false)
    console.debug('items', this.items)

    this.container.addEventListener('click', this.onClick)
  }

  public getItems(): CartItem[] {
    return this.items
  }

  public setDeliveryMethod(method: DeliveryMethod): void {
    this.deliveryCost = method === 'courier' ? this.baseDeliveryCost : 0

    if (this.deliveryCostElement) {
      renderWithCurrency(
        this.deliveryCostElement,
        this.deliveryCost.toLocaleString('ru-RU', {
          style: 'currency',
          currency: this.items[0]?.currency || 'RUB',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
      )
    }

    this.calculateTotalPrice(true)
  }

  public reset(): void {
    this.clearCart()
  }

  private updateItems(announceTotalPrice: boolean = false) {
    this.itemsContainer.innerHTML = ''
    this.items = getCartItems()
    this.renderItems()
    this.calculateTotalPrice(announceTotalPrice)

    if (this.onItemsChange) {
      this.onItemsChange(this.items)
    }
  }

  private renderItems() {
    const documentFragment = document.createDocumentFragment()
    this.items.forEach((item) => {
      const itemElement = this.createCartItemElement(item)
      documentFragment.appendChild(itemElement)
    })
    this.itemsContainer.appendChild(documentFragment)

    const counterFields = this.container.querySelectorAll(
      '[data-counter-field]'
    )

    counterFields.forEach((field) => {
      new CounterField(field as HTMLElement)
    })
  }

  private calculateTotalPrice(announceTotalPrice: boolean = false) {
    const totalLabel =
      this.container.querySelector<HTMLElement>('[data-total-label]')
    if (totalLabel)
      totalLabel.textContent = this.items.some(
        (item) => item.currency === 'BYN'
      )
        ? 'Номинал сертификатов'
        : 'К оплате'
    const itemsTotal = this.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    )
    if (new Set(this.items.map((item) => item.currency || 'RUB')).size > 1) {
      this.totalPriceElement.textContent =
        'Сертификаты разных валют: оформите отдельные заказы'
      return
    }
    this.totalPrice = itemsTotal + this.deliveryCost - this.discountAmount
    this.updateTotalPriceUI(announceTotalPrice)
  }

  private updateTotalPriceUI(announceTotalPrice: boolean = false) {
    if (this.totalPriceElement) {
      const totalPriceText = this.totalPrice.toLocaleString('ru-RU', {
        style: 'currency',
        currency: this.items[0]?.currency || 'RUB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })

      const hasChanged = this.totalPriceElement.textContent !== totalPriceText

      if (announceTotalPrice && hasChanged) {
        this.totalPriceElement.setAttribute('aria-live', 'polite')
      }

      renderWithCurrency(this.totalPriceElement, totalPriceText)

      if (announceTotalPrice && hasChanged) {
        if (this.liveRegionResetTimer) {
          window.clearTimeout(this.liveRegionResetTimer)
        }

        this.liveRegionResetTimer = window.setTimeout(() => {
          this.totalPriceElement.setAttribute('aria-live', 'off')
          this.liveRegionResetTimer = null
        }, 1000)
      }
    }
  }

  private onCounterChange = (event: CustomEvent) => {
    const { name, value } = event.detail

    const counterEl = this.container.querySelector<HTMLElement>(
      `[data-counter-field][data-name="${name}"]`
    )
    if (counterEl) {
      const itemId = counterEl
        .closest('[data-cart-item]')
        ?.getAttribute('data-id')
      if (itemId) {
        const item = this.items.find((i) => i.id.toString() === itemId)
        if (item) {
          item.quantity = value
          this.calculateTotalPrice(true)
          updateCartItem(item)
        }
      }
    }
  }

  private onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement

    if (target.closest('[data-cart-item-delete]')) {
      this.deleteItem(target.closest('[data-cart-item]') as HTMLElement)
    }

    if (target.closest('[data-delete-all]')) {
      this.clearCart()
    }
  }

  private deleteItem(itemEl: HTMLElement) {
    if (itemEl) {
      const itemId = itemEl.getAttribute('data-id')
      if (itemId) {
        const item = this.items.find((i) => i.id.toString() === itemId)
        if (item) {
          removeFromCart(item)
          this.updateItems(true)
        }
      }
    }
  }

  private clearCart() {
    clearCart()
    this.updateItems(true)
  }

  private createCartItemElement(item: CartItem): HTMLElement {
    const template = document.createElement('template')
    template.innerHTML = this.createCartItemTemplate(item).trim()
    return template.content.firstElementChild as HTMLElement
  }

  private createCartItemTemplate(item: CartItem): string {
    const parameters = Object.values(item.parameters || {}).join(', ')
    const note =
      item.type === 'digital'
        ? '<p class="cart-item__text">придёт на почту</p>'
        : ''

    const counterName = `cart-item-${item.id}-quantity`

    return `<div class="cart-item" data-cart-item data-id="${item.id}">
              <div class="cart-item__img">
                <img loading="lazy" src="${item.image}" alt="${item.title}">
              </div>

              <div class="cart-item__info">
                <button type="button" class="cart-item__btn-delete" data-cart-item-delete>
                  <span class="visually-hidden">Удалить товар из корзины</span>
                  <svg>
                    <use href="#close"></use>
                  </svg>
                </button>

                <p class="cart-item__title">
                  ${item.title}
                </p>

                ${note}

                <p class="cart-item__text">
                  ${parameters}
                </p>

                <div class="field cart-item__counter-field" data-field data-field-name="${counterName}">
                  <label for="${counterName}" class="field__label">${item.price} ${currencySignHtml(item.currency === 'BYN' ? 'BYN' : '₽')}</label>

                  <div class="field__wrapper" data-field-wrapper>

                    <div class="counter-field" data-counter-field="" data-name="${counterName}" data-min-value="1">
                      <button type="button" class="counter-field__button" aria-label="Уменьшить на 1" data-counter-decrease>
                        <svg width="12" height="12">
                          <use href="#minus"></use>
                        </svg>
                      </button>

                      <span data-counter-display>${item.quantity}</span>

                      <button type="button" class="counter-field__button" aria-label="Увеличить на 1" data-counter-increase>
                        <svg width="12" height="12">
                          <use href="#plus"></use>
                        </svg>
                      </button>

                      <input type="text" class="visually-hidden" name="${counterName}" id="${counterName}" value="${item.quantity}" data-counter-input>
                    </div>

                  </div>

                  <span class="field__error-message" data-error></span>
                </div>
                </div>
              </div>`
  }
}
